import React from "react";
import {
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

interface SmartAlertProps {
  type: "info" | "warning" | "error" | "success";
  message: string;
  actions?: Array<{ label: string; onClick: () => void }>;
  onDismiss?: () => void;
}

export const SmartAlert: React.FC<SmartAlertProps> = ({
  type,
  message,
  actions,
  onDismiss,
}) => {
  const styles = {
    info: {
      container: "bg-blue-50 border-blue-200",
      icon: InformationCircleIcon,
      iconColor: "text-blue-400",
      text: "text-blue-800",
      button: "bg-blue-100 text-blue-900 hover:bg-blue-200",
    },
    warning: {
      container: "bg-amber-50 border-amber-200",
      icon: ExclamationTriangleIcon,
      iconColor: "text-amber-400",
      text: "text-amber-800",
      button: "bg-amber-100 text-amber-900 hover:bg-amber-200",
    },
    error: {
      container: "bg-red-50 border-red-200",
      icon: XCircleIcon,
      iconColor: "text-red-400",
      text: "text-red-800",
      button: "bg-red-100 text-red-900 hover:bg-red-200",
    },
    success: {
      container: "bg-green-50 border-green-200",
      icon: CheckCircleIcon,
      iconColor: "text-green-400",
      text: "text-green-800",
      button: "bg-green-100 text-green-900 hover:bg-green-200",
    },
  };

  const style = styles[type];
  const Icon = style.icon;

  return (
    <div className={`rounded-lg border p-4 ${style.container}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${style.iconColor}`} />
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${style.text}`}>{message}</p>
          {actions && actions.length > 0 && (
            <div className="mt-3 flex gap-2">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md ${style.button} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <button
              onClick={onDismiss}
              className={`inline-flex rounded-md p-1.5 ${style.text} hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-offset-2`}
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
